/**
 * Highlight-lag metric: how far behind the audio the rendered highlight is.
 * `lagMs = position at commit time − active word's start_ms`, sampled on every
 * rendered active-word change; `skipped` counts words the highlight jumped
 * over while playing (index advanced by more than one, no seek involved).
 *
 * Pure TS with a pluggable per-chunk summary sink, so the mobile app can
 * forward summaries through its Effect runtime while the recorder itself
 * stays shared and testable in the node environment.
 */

/** `chunkKey` + `startMs` + `seekCount` identify one committed advance: the
 * read-along hooks are mounted several times over the same chunk, so the same
 * advance is reported once per mount, while a revisit of the same word is only
 * reachable through a seek. */
export type HighlightLagSample = {
  chunkKey: unknown;
  startMs: number;
  seekCount: number | undefined;
  lagMs: number;
  skipped: number;
};

export type HighlightLagStats = {
  sampleCount: number;
  skippedWords: number;
  p50Ms: number | null;
  p95Ms: number | null;
  maxMs: number | null;
  lastMs: number | null;
};

const MAX_SESSION_SAMPLES = 2048;

function percentileOf(sorted: readonly number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.ceil((p / 100) * sorted.length) - 1),
  );
  return sorted[index] ?? null;
}

function statsOf(
  lags: readonly number[],
  skippedWords: number,
): HighlightLagStats {
  const sorted = [...lags].toSorted((a, b) => a - b);
  return {
    sampleCount: lags.length,
    skippedWords,
    p50Ms: percentileOf(sorted, 50),
    p95Ms: percentileOf(sorted, 95),
    maxMs: sorted.at(-1) ?? null,
    lastMs: lags.at(-1) ?? null,
  };
}

function isSameAdvance(a: HighlightLagSample, b: HighlightLagSample) {
  return (
    a.chunkKey === b.chunkKey &&
    a.startMs === b.startMs &&
    a.seekCount === b.seekCount
  );
}

export function createHighlightLagRecorder() {
  let sessionLags: number[] = [];
  let sessionSkipped = 0;
  let chunkLags: number[] = [];
  let chunkSkipped = 0;
  let currentChunkKey: unknown;
  let lastSample: HighlightLagSample | null = null;
  let onChunkSummary: ((summary: HighlightLagStats) => void) | null = null;

  const rollChunk = (nextKey: unknown) => {
    if (chunkLags.length > 0 && onChunkSummary) {
      onChunkSummary(statsOf(chunkLags, chunkSkipped));
    }
    chunkLags = [];
    chunkSkipped = 0;
    currentChunkKey = nextKey;
  };

  return {
    record(sample: HighlightLagSample) {
      if (lastSample !== null && isSameAdvance(lastSample, sample)) return;
      lastSample = sample;
      if (sample.chunkKey !== currentChunkKey) rollChunk(sample.chunkKey);
      chunkLags.push(sample.lagMs);
      chunkSkipped += sample.skipped;
      sessionLags.push(sample.lagMs);
      sessionSkipped += sample.skipped;
      if (sessionLags.length > MAX_SESSION_SAMPLES) {
        sessionLags = sessionLags.slice(-MAX_SESSION_SAMPLES);
      }
    },
    sessionStats(): HighlightLagStats {
      return statsOf(sessionLags, sessionSkipped);
    },
    setChunkSummarySink(sink: ((summary: HighlightLagStats) => void) | null) {
      onChunkSummary = sink;
    },
    reset() {
      sessionLags = [];
      sessionSkipped = 0;
      chunkLags = [];
      chunkSkipped = 0;
      currentChunkKey = undefined;
      lastSample = null;
    },
  };
}

export const highlightLagRecorder = createHighlightLagRecorder();
